/**
 * Professional Directory (Bucket C — "ابحث عن").
 *
 * A searchable public directory of the five licensed professional types
 * (broker/marketer, property-mgmt, facilities-mgmt, appraiser, consultant).
 * Filter by city + individual/company; each result links to the unified
 * /professionals/:id profile. Built from the trusted-brokers patterns + the
 * shared theme; flag-gated via the backend (router 404s → graceful empty state).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Pagination from '../../components/base/Pagination';
import SegmentedControl from '../../components/base/SegmentedControl';
import ProfessionalCard from './ProfessionalCard';
import {
  fetchProfessionals,
  DIRECTORY_TYPES,
  DIRECTORY_TYPE_VALUES,
  directoryTypeDef,
  type ProfessionalListItem,
  type DirectoryActivityType,
  type OwnerType,
} from '../../services/professionalsService';
import { BrandRule } from '../../components/brand/BrandRule';

const PAGE_SIZE = 12;

const CITIES = [
  { value: '', en: 'All cities', ar: 'كل المدن' },
  { value: 'Riyadh', en: 'Riyadh', ar: 'الرياض' },
  { value: 'Jeddah', en: 'Jeddah', ar: 'جدة' },
  { value: 'Makkah', en: 'Makkah', ar: 'مكة المكرمة' },
  { value: 'Madinah', en: 'Madinah', ar: 'المدينة المنورة' },
  { value: 'Dammam', en: 'Dammam', ar: 'الدمام' },
];

// The search page's pill recipe (THEME.md decision D3 for the chosen one).
const PILL = 'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-4 text-[0.875rem] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30';
const PILL_ON = 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]';
const PILL_OFF = 'border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] text-[var(--pv-color-text)] hover:border-[var(--pv-color-primary)]';

const OWNER_FILTERS: { value: '' | OwnerType; en: string; ar: string }[] = [
  { value: '', en: 'All', ar: 'الكل' },
  { value: 'individual', en: 'Individuals', ar: 'أفراد' },
  { value: 'company', en: 'Companies', ar: 'شركات' },
];

export default function ProfessionalsDirectoryPage() {
  const { i18n } = useTranslation('common');
  const isRTL = i18n.dir() === 'rtl';
  const [searchParams, setSearchParams] = useSearchParams();

  const typeParam = searchParams.get('type') as DirectoryActivityType | null;
  const activeType: DirectoryActivityType = typeParam && DIRECTORY_TYPE_VALUES.includes(typeParam) ? typeParam : 'BROKER';

  const [city, setCity] = useState('');
  const [ownerType, setOwnerType] = useState<'' | OwnerType>('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ProfessionalListItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Reset paging when the type/filters change.
  useEffect(() => { setPage(1); }, [activeType, city, ownerType]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchProfessionals({ activityType: activeType, city: city || undefined, ownerType: ownerType || undefined, page, pageSize: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data);
        setTotalPages(res.meta.totalPages || 1);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setTotalPages(1);
        setError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeType, city, ownerType, page]);

  const selectType = (t: DirectoryActivityType) => {
    const next = new URLSearchParams(searchParams);
    next.set('type', t);
    setSearchParams(next);
  };

  const goToPage = (target: number) => {
    const next = Math.min(Math.max(1, target), totalPages);
    if (next !== page) { setPage(next); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };
  // SegmentedControl values are strings; '' (all) is a valid one.
  type OwnerValue = '' | OwnerType;

  const activeDef = directoryTypeDef(activeType);

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={i18n.dir()}>
      <Navigation />
      <main id="main-content" className="pb-20">
        {/* Header: the title, the five directories and the filters, in the same
            calm pattern as /search and the brokers page (owner, 2026-10-07; it
            was a deep-green band over rows of solid pills). */}
        <header className="border-b border-[var(--pv-border-subtle)]">
          <div className="mx-auto max-w-6xl px-4 pb-5 pt-8 sm:px-6">
            <h1 className="text-[1.625rem] font-bold text-[var(--pv-color-primary)] sm:text-[2rem]">
              {isRTL ? 'ابحث عن مختصّ عقاري' : 'Find a real-estate professional'}
            </h1>
            <BrandRule className="mt-3" />
            <p className="mt-1.5 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
              {isRTL
                ? 'وسطاء ومسوّقون، مديرو أملاك ومرافق، مقيّمون ومستشارون - موثّقون ومرخّصون.'
                : 'Brokers & marketers, property & facilities managers, appraisers & consultants - verified & licensed.'}
            </p>

            {/* The five directories */}
            <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar" role="group" aria-label={isRTL ? 'نوع المختص' : 'Professional type'}>
              {DIRECTORY_TYPES.map((d) => {
                const active = d.type === activeType;
                return (
                  <button
                    key={d.type}
                    type="button"
                    onClick={() => selectType(d.type)}
                    aria-pressed={active}
                    className={`${PILL} ${active ? PILL_ON : PILL_OFF}`}
                  >
                    <i className={`${d.icon} text-base leading-none`} aria-hidden="true"></i>
                    {isRTL ? d.ar : d.en}
                  </button>
                );
              })}
            </div>

            {/* Filters: city + individual / company */}
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-2 overflow-x-auto no-scrollbar" role="group" aria-label={isRTL ? 'المدينة' : 'City'}>
                {CITIES.map((c) => (
                  <button
                    key={c.value || 'all'}
                    type="button"
                    aria-pressed={city === c.value}
                    onClick={() => setCity(c.value)}
                    className={`${PILL} ${city === c.value ? PILL_ON : PILL_OFF}`}
                  >
                    {isRTL ? c.ar : c.en}
                  </button>
                ))}
              </div>
              <SegmentedControl<OwnerValue>
                value={ownerType}
                onChange={setOwnerType}
                options={OWNER_FILTERS.map((o) => ({ value: o.value, label: isRTL ? o.ar : o.en }))}
                ariaLabel={isRTL ? 'فرد أو شركة' : 'Individual or company'}
                size="sm"
              />
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-4 pt-6 sm:px-6">
          {loading ? (
            // The same grid as the result, so nothing jumps when it lands.
            <ul role="list" aria-hidden="true" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <li key={i} className="h-[11.5rem] animate-pulse rounded-[16px] bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
              ))}
            </ul>
          ) : items.length === 0 ? (
            <div className="mx-auto max-w-md py-16 text-center">
              <i
                className={`${error ? 'ri-wifi-off-line' : activeDef?.icon || 'ri-user-search-line'} text-[2.25rem] leading-none text-[var(--pv-color-text-muted)]`}
                aria-hidden="true"
              ></i>
              <h2 className="mt-4 text-[1.25rem] font-semibold text-[var(--pv-color-text)]">
                {error
                  ? (isRTL ? 'تعذّر تحميل الدليل' : 'Could not load the directory')
                  : (isRTL ? 'لا يوجد مختصّون بعد' : 'No professionals yet')}
              </h2>
              <p className="mt-2 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                {error
                  ? (isRTL ? 'حاول مرة أخرى لاحقاً.' : 'Please try again later.')
                  : (isRTL ? 'سيظهر المختصّون الموثّقون هنا في هذه الفئة والمدينة قريباً.' : 'Verified professionals in this category & city will appear here soon.')}
              </p>
            </div>
          ) : (
            <ul role="list" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((pro) => (
                <li key={pro.id}>
                  <ProfessionalCard pro={pro} isRTL={isRTL} />
                </li>
              ))}
            </ul>
          )}

          {!loading && totalPages > 1 && (
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={goToPage} className="mt-10" />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
